import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ClipboardCheck, RefreshCw, Trash2, Plus, X, ArrowUpDown } from 'lucide-react'
import { clsx } from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { api } from '../lib/api'
import TTEBadge, { type TTEData } from '../components/TTEBadge'

interface TriageRow {
  id: number
  cve_id: string
  status: string
  priority: string
  owner: string
  due_date: string | null
  notes: string
  affected_services: string[]
  created_at: string
  updated_at: string
}

const STATUS_OPTIONS = ['open', 'investigating', 'mitigating', 'patched', 'risk_accepted', 'closed']
const PRIORITY_OPTIONS = ['critical', 'high', 'medium', 'low']

const STATUS_TONE: Record<string, string> = {
  open:           'bg-rose/15 text-rose-light border-rose/40',
  investigating:  'bg-amber/15 text-amber-light border-amber/40',
  mitigating:     'bg-blue/15 text-blue-light border-blue/40',
  patched:        'bg-emerald/15 text-emerald border-emerald/40',
  risk_accepted:  'bg-purple-500/15 text-purple-300 border-purple-500/40',
  closed:         'bg-surface-raised text-text-muted border-border',
}

export default function VulnTriagePage() {
  const [rows, setRows] = useState<TriageRow[]>([])
  const [stats, setStats] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({ cve_id: '', status: 'open', priority: 'medium', owner: '', due_date: '', notes: '' })
  const [tteMap, setTteMap] = useState<Record<string, TTEData | null>>({})
  // Sort by TTE ("weaponised soonest first") instead of update time.
  const [sortByTte, setSortByTte] = useState(false)

  const fetchAll = useCallback(async () => {
    setLoading(true)
    try {
      const [r, s] = await Promise.allSettled([
        api.get('/api/cyber/triage', { params: { limit: 200 } }),
        api.get('/api/cyber/triage/stats'),
      ])
      if (r.status === 'fulfilled') setRows(r.value.data?.items || [])
      if (s.status === 'fulfilled') setStats(s.value.data)
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { fetchAll() }, [fetchAll])

  // Batch-fetch TTE for the triaged CVEs so the queue can be ordered by
  // "weaponised soonest" — the velocity-first triage the TTE thesis is about.
  useEffect(() => {
    const ids = Array.from(new Set(rows.map(r => r.cve_id).filter(Boolean)))
    if (ids.length === 0) return
    let mounted = true
    api.get('/api/cyber/tte-batch', { params: { cves: ids.join(',') } })
      .then(r => { if (mounted) setTteMap(r.data || {}) })
      .catch(() => { /* fail-soft: TTE column shows — */ })
    return () => { mounted = false }
  }, [rows])

  const sortedRows = useMemo(() => {
    if (!sortByTte) return rows
    const tteOf = (cve: string) => {
      const t = tteMap[cve?.toUpperCase()] ?? tteMap[cve]
      return t && t.predicted_tte_days != null ? t.predicted_tte_days : Number.POSITIVE_INFINITY
    }
    return [...rows].sort((a, b) => tteOf(a.cve_id) - tteOf(b.cve_id))
  }, [rows, sortByTte, tteMap])

  const upsert = async () => {
    if (!form.cve_id) return
    try {
      await api.post('/api/cyber/triage', {
        ...form,
        due_date: form.due_date ? new Date(form.due_date).toISOString() : null,
        affected_services: [],
      })
      setShowForm(false)
      setForm({ cve_id: '', status: 'open', priority: 'medium', owner: '', due_date: '', notes: '' })
      fetchAll()
    } catch {}
  }

  const inlineUpdate = async (row: TriageRow, patch: Partial<TriageRow>) => {
    try {
      await api.post('/api/cyber/triage', {
        cve_id:   row.cve_id,
        status:   patch.status || row.status,
        priority: patch.priority || row.priority,
        owner:    patch.owner ?? row.owner,
        due_date: patch.due_date ?? row.due_date,
        notes:    row.notes,
        affected_services: row.affected_services || [],
      })
      fetchAll()
    } catch {}
  }

  const remove = async (cve_id: string) => {
    if (!confirm(`Untriage ${cve_id}?`)) return
    await api.delete(`/api/cyber/triage/${encodeURIComponent(cve_id)}`)
    fetchAll()
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <ClipboardCheck size={24} className="text-emerald" /> Vulnerability Triage
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Per-CVE assignment + status + due date. Hooks into top-risk feed.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={fetchAll} className="btn-ghost text-xs"><RefreshCw size={12} className={loading ? 'animate-spin' : ''} /></button>
          <button type="button" onClick={() => setShowForm(true)} className="btn-primary text-xs"><Plus size={12} /> Add CVE</button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Tile label="Open" value={stats.by_status?.open || 0} tone="rose" />
          <Tile label="In progress" value={(stats.by_status?.investigating || 0) + (stats.by_status?.mitigating || 0)} tone="amber" />
          <Tile label="Patched" value={stats.by_status?.patched || 0} tone="emerald" />
          <Tile label="Overdue" value={stats.overdue || 0} tone="rose" pulse={stats.overdue > 0} />
        </div>
      )}

      {showForm && (
        <div className="card p-4 border border-blue/40">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-text-primary">Add to triage</h3>
            <button type="button" onClick={() => setShowForm(false)} className="text-text-muted hover:text-text-primary"><X size={14} /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
            <input value={form.cve_id} onChange={e => setForm({...form, cve_id: e.target.value.toUpperCase()})} placeholder="CVE-2024-XXXXX" className="input py-1.5" />
            <select value={form.priority} onChange={e => setForm({...form, priority: e.target.value})} className="input py-1.5">
              {PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="input py-1.5">
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <input value={form.owner} onChange={e => setForm({...form, owner: e.target.value})} placeholder="Owner email" className="input py-1.5" />
            <input type="datetime-local" value={form.due_date} onChange={e => setForm({...form, due_date: e.target.value})} className="input py-1.5" />
            <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Notes" rows={2} className="input py-1.5 md:col-span-2" />
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button type="button" onClick={() => setShowForm(false)} className="btn-ghost text-xs">Cancel</button>
            <button type="button" onClick={upsert} className="btn-primary text-xs">Save</button>
          </div>
        </div>
      )}

      <div className="card p-3 overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-text-muted border-b border-border">
              <th className="text-left py-2 px-2">CVE</th>
              <th className="text-left py-2 px-2">
                <button type="button" onClick={() => setSortByTte(v => !v)}
                  title="Time-to-Exploit — predicted weaponization window. Click to sort by 'weaponised soonest'."
                  className={clsx('inline-flex items-center gap-1 uppercase tracking-wider hover:text-text-primary',
                    sortByTte ? 'text-rose-light' : 'text-text-muted')}>
                  TTE <ArrowUpDown size={10} />
                </button>
              </th>
              <th className="text-left py-2 px-2">Priority</th>
              <th className="text-left py-2 px-2">Status</th>
              <th className="text-left py-2 px-2">Owner</th>
              <th className="text-left py-2 px-2">Due</th>
              <th className="text-left py-2 px-2">Updated</th>
              <th className="text-right py-2 px-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={8} className="text-center py-6 text-text-muted text-[11px] italic">
                No triaged CVEs yet — add one or import from /app/cyber.
              </td></tr>
            )}
            {sortedRows.map(r => {
              const overdue = r.due_date && new Date(r.due_date) < new Date() && !['patched', 'closed', 'risk_accepted'].includes(r.status)
              const tte = tteMap[r.cve_id?.toUpperCase()] ?? tteMap[r.cve_id]
              return (
                <tr key={r.id} className="border-b border-border/50 hover:bg-surface-hover">
                  <td className="py-1.5 px-2">
                    <Link to={`/app/cve/${encodeURIComponent(r.cve_id)}`} className="font-mono text-blue-light hover:underline">{r.cve_id}</Link>
                  </td>
                  <td className="py-1.5 px-2"><TTEBadge tte={tte} /></td>
                  <td className="py-1.5 px-2">
                    <select value={r.priority} onChange={e => inlineUpdate(r, { priority: e.target.value })}
                      className="input text-[10px] py-0.5">
                      {PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </td>
                  <td className="py-1.5 px-2">
                    <span className={clsx('px-1.5 py-0.5 rounded border text-[9px] font-mono uppercase', STATUS_TONE[r.status] || STATUS_TONE.open)}>
                      <select value={r.status} onChange={e => inlineUpdate(r, { status: e.target.value })}
                        className="bg-transparent border-none text-inherit text-[9px] uppercase font-mono outline-none">
                        {STATUS_OPTIONS.map(s => <option key={s} value={s} className="text-text-primary">{s}</option>)}
                      </select>
                    </span>
                  </td>
                  <td className="py-1.5 px-2">{r.owner || <span className="text-text-muted italic">—</span>}</td>
                  <td className={clsx('py-1.5 px-2 font-mono text-[10px]', overdue && 'text-rose-light font-bold')}>
                    {r.due_date ? new Date(r.due_date).toLocaleDateString() : '—'}
                    {overdue && ' ⚠'}
                  </td>
                  <td className="py-1.5 px-2 text-[10px] text-text-muted">{formatDistanceToNow(new Date(r.updated_at), { addSuffix: true })}</td>
                  <td className="py-1.5 px-2 text-right">
                    <button type="button" onClick={() => remove(r.cve_id)} className="text-text-muted hover:text-rose"><Trash2 size={11} /></button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function Tile({ label, value, tone, pulse }: { label: string; value: number; tone: string; pulse?: boolean }) {
  const t = tone === 'rose' ? 'text-rose' : tone === 'amber' ? 'text-amber-light' : tone === 'emerald' ? 'text-emerald' : 'text-text-primary'
  return (
    <div className="card p-3">
      <div className="text-[10px] uppercase tracking-wider text-text-muted">{label}</div>
      <div className={clsx('text-2xl font-bold mt-1 font-mono', t, pulse && 'animate-pulse')}>{value}</div>
    </div>
  )
}
